import {createFileRoute, useNavigate} from "@tanstack/react-router";
import {Box, Button, Stack, ToggleButtonGroup, useTheme} from "@mui/joy";
import {useTranslation} from "react-i18next";
import {useEffect, useMemo, useState} from "react";
import {AgentStub, Transfer} from "../../../rpc/webrpc-client.gen.ts";
import {useSelf} from "../../../agent/useSelf.ts";
import {ShinwaJobCapsule} from "../../../job/capsule.tsx";
import {VenueCapsule} from "../../../venue/capsule.tsx";
import {cmpScenesNewestFirst} from "../../../timeline/timeline.tsx";
import {DriverBundleTile} from "../../../driver/DriverBundleTile.tsx";
import {useScheduledTransfersForDriver} from "../../../driver/schedule.ts";
import {SuspenseDialog} from "../../../components/SuspenseDialog.tsx";
import {useOpenLocalBundleTransfers} from "./next.tsx";
import {faPersonCircleCheck, faPersonDolly} from "@fortawesome/pro-regular-svg-icons";
import {faPersonCircleQuestion} from "@fortawesome/pro-solid-svg-icons";
import {TileSpread} from "../../../components/Tile.tsx";
import {
    ToggleBar,
    ToggleBarIcon,
    toggleIconCounterSx,
    toggleSwitchButtonSx,
    toggleSwitchSx
} from "../../../components/ToggleBar.tsx";
import {DisclaimScheduledTransferDialog} from "../../../driver/claimScheduledTransfer.tsx";
import {DriverScheduleScene, synthDriverScheduleScenes} from "../../../agent/timeline.tsx";
import {SceneViewProps} from "../../../timeline/sceneViewProps.tsx";
import {DiarySceneView, DiaryView} from "../../../timeline/diary.tsx";
import {PageBody, PageHeader, PageShell} from "../../../routing/shell.tsx";
import {TopBar} from "../../../components/TopBar.tsx";

const OrphanDriver: AgentStub = {
    id: "<deadbeef>",
    displayName: "nobody",
    code: "<deadbeef>",
};

const scheduledTransferBucketIcons = {
    open: faPersonDolly,
    scheduled: faPersonCircleCheck,
    orphaned: faPersonCircleQuestion,
} as const;
export type ScheduledTransferBucket = keyof typeof scheduledTransferBucketIcons;

// interface ScheduledBundleTransferTimelineEntryProps
//     extends Omit<TimelineSceneViewProps, "when" | "what"> {
//     transfer: ScheduledTransfer;
// }

export function DriverScheduleSceneView(
    {
        scene,
        color: inColor,
        variant: inVariant,
        ref, dataIndex,
        ...rest
    }: SceneViewProps<DriverScheduleScene>) {
    const {data: self} = useSelf();
    const [confirmingDisclaim, setConfirmingDisclaim]
        = useState<boolean>(false);
    // const handleLongPress = useCallback(() => {
    //     if (props.transfer.driver?.id === self.id) {
    //         setConfirmingDisclaim(true);
    //     }
    // }, [props.transfer.driver?.id, self.id]);
    // const bindLongPress = useLongPress(handleLongPress)

    const theme = useTheme();
    const navigate = useNavigate();
    const sxfer = scene.scheduledTransferScene.subject;
    const color = inColor ??
        (scene.subject.id === self?.id
            ? "primary" : "warning");
    const variant = inVariant ?? "soft";

    return <>
        <DiarySceneView scene={scene} color={color} variant={variant}
                        onClick={() => {
                            void navigate({
                                to: "/sxfer/$id",
                                params: {id: sxfer.id}
                            });
                        }} ref={ref} dataIndex={dataIndex}
                        {...rest}>
            <Stack direction="column"
                   sx={{alignItems: "center", px: 2, cursor: "pointer", width: "95%"}}>
                <ShinwaJobCapsule sx={{fontSize: theme.fontSize.xl, color: theme.palette.primary[800]}}
                                  job={sxfer.job} {...rest}/>
                <VenueCapsule venue={{displayName: sxfer.venue.displayName}} {...rest}/>
            </Stack>
        </DiarySceneView>
        <DisclaimScheduledTransferDialog
            transfer={sxfer}
            isOpen={confirmingDisclaim} close={() => {
            setConfirmingDisclaim(false);
        }}/>
    </>
}

// function subheader(label: string) {
//     return <ListSubheader>
//         <Sheet color="primary" variant="soft" invertedColors
//                sx={{
//                    width: "100%", display: "flex",
//                    flexDirection: "row", justifyContent: "center"
//                }}>
//             <Box sx={{fontSize: "xl"}}>{label}</Box>
//         </Sheet>
//     </ListSubheader>
// }

export function DriverSchedule() {
    const {t} = useTranslation();
    const selfTqrv = useSelf();
    const self = selfTqrv.data;
    const myScheduledTransfersTqrv
        = useScheduledTransfersForDriver(self ? {driverId: self.id} : undefined);
    const myScenes = useMemo(() => {
        if (!self || !myScheduledTransfersTqrv.data) {
            return undefined;
        }
        return synthDriverScheduleScenes({
            driver: self,
            scheduledTransfers: myScheduledTransfersTqrv.data
        }).toSorted(cmpScenesNewestFirst);
    }, [myScheduledTransfersTqrv.data, self]);

    // const queryFn = useCallback((webrpcOptions: WebrpcStreamOptions<{
    //     packet: SetlistPacket<ScheduledBundleTransfer>
    // }>) => {
    //     console.log("self schedule");
    //     return GLOBAL_MOTHERSHIP.scheduledBundleTransfersForDriver({driverId: selfId}, webrpcOptions)
    //     // return Promise.resolve();
    // }, [selfId]);
    //
    // const {
    //     setlist: myScheduledTransfers,
    //     connectionStatus: myScheduledTransferConnectionStatus
    // } = useSetlist<ScheduledBundleTransfer>(
    //     {queryFn, cmpT: cmpScheduledBundleTransfer});

    // const {data: freeScheduledTransfersData, isSuccess: isFreeScheduledTransfersSuccess}
    //     = useRefreshableQuery(scheduledBundleTransfersForDriverTqo({driverId: undefined}));
    // const {
    //     setlist: freeScheduledTransfers,
    //     connectionStatus: freeScheduledTransferConnectionStatus
    // } = useSetlist<ScheduledBundleTransfer>(
    //     {
    //         queryFn: (webrpcOptions) => {
    //             console.log("orphan schedule");
    //             // return GLOBAL_MOTHERSHIP.scheduledBundleTransfersForDriver({}, webrpcOptions);
    //             return Promise.resolve();
    //         },
    //     });
    const {data: freeScheduledTransfers} = useScheduledTransfersForDriver({driverId: ""});
    const freeScenes = useMemo(() => {
        if (!freeScheduledTransfers) {
            return undefined;
        }
        return synthDriverScheduleScenes({
            driver: OrphanDriver,
            scheduledTransfers: freeScheduledTransfers,
        }).toSorted(cmpScenesNewestFirst);

    }, [freeScheduledTransfers]);

    const openLocalTransfersTqrv = useOpenLocalBundleTransfers();
    const openLocalTransfers = openLocalTransfersTqrv.data;
    // const bxtTqrv = useBundleExpandedTransfers(
    //     openLocalTransfersTqrv.data ? {transfers: openLocalTransfersTqrv.data} : undefined);
    // const unsortedUncleanBxt = cleanse(bxtTqrv.data);
    // const firstUnbundledBxt = unsortedUncleanBxt.find(maybe => !maybe.bundle);
    // if (firstUnbundledBxt) {
    //     throw new Error(`transfer ${firstUnbundledBxt.transfer.id} has no active bundle`);
    // }
    // const unsortedCleanBxt = unsortedUncleanBxt as BundleExpandedTransfer[];
    // const sortedBxt =
    //     useMemo(() => sortedClone(unsortedCleanBxt), [unsortedCleanBxt]);

    const [bucket, setBucket] = useState<ScheduledTransferBucket>("scheduled");
    const [isBucketChosen, setIsBucketChosen] = useState<boolean>(false);
    useEffect(() => {
        if (!isBucketChosen) {
            if (openLocalTransfers && openLocalTransfers.length > 0) {
                setBucket("open");
            }
        }
    }, [isBucketChosen, openLocalTransfers]);

    const numOpen = openLocalTransfers?.length ?? 0;
    const numOprhaned = freeScheduledTransfers?.length ?? 0;

    const content = useMemo(() => {
        switch (bucket) {
            case "open": {
                return <TileSpread foods={openLocalTransfers ?? []}
                                   estimatedHeight={240} Component={({food: xfer}: { food: Transfer }) =>
                    <DriverBundleTile bundleStub={xfer.bundle} key={xfer.id}
                                      color="primary" variant="outlined"/>}/>;
            }

            case "scheduled": {
                if (!myScenes) {
                    return <SuspenseDialog message="my scheduled transfers"/>;
                }
                return <DiaryView sortedScenes={myScenes} Entry={DriverScheduleSceneView}/>
            }

            case "orphaned": {
                if (!freeScenes) {
                    return <SuspenseDialog message="free scheduled transfers"/>;
                }
                return <DiaryView sortedScenes={freeScenes} Entry={DriverScheduleSceneView}/>;
            }
        }
    }, [bucket, freeScenes, myScenes, openLocalTransfers]);

//    const isOpenLocalBundleTransfersSuccess = true;
//    const myScheduledTransfers = NADA;
//    const freeScheduledTransfers = NADA;
//     const openLocalBundleTransfers = NADA;

    if (!selfTqrv.isSuccess) {
        return <SuspenseDialog message={t("loadingSelf")}/>
    }
    if (!openLocalTransfersTqrv.isSuccess) {
        return <SuspenseDialog message="open local transfers"/>;
    }
    if (!myScheduledTransfersTqrv.isSuccess) {
        return <SuspenseDialog message="my scheduled transfers"/>;
    }
    // if (!freeScheduledTransfers) {
    //     return <SuspenseDialog message="orphaned scheduled transfers"/>;
    // }

    return <PageShell>
        <PageHeader>
            <Stack direction="column">
                <TopBar/>

                <ToggleBar>
                    <ToggleButtonGroup value={bucket} onChange={(_, next) => {
                        setBucket(next ?? "scheduled");
                        setIsBucketChosen(true);
                    }} sx={toggleSwitchSx} color="primary" variant="soft">
                        {numOpen > 0
                            ? <Button value="open" sx={toggleSwitchButtonSx}>
                        <span className="fa-layers fa-fw">
                            <ToggleBarIcon icon={scheduledTransferBucketIcons.open}/>
                            <Box sx={toggleIconCounterSx}>{numOpen}</Box>
                        </span>
                            </Button>
                            : <Button value="open" disabled={true} sx={toggleSwitchButtonSx}>
                                <ToggleBarIcon icon={scheduledTransferBucketIcons.open}/>
                            </Button>
                        }

                        <Button value="scheduled" sx={toggleSwitchButtonSx}>
                            <ToggleBarIcon icon={scheduledTransferBucketIcons.scheduled}/>
                        </Button>

                        {numOprhaned > 0
                            ? <Button value="orphaned" sx={toggleSwitchButtonSx}>
                        <span className="fa-layers fa-fw">
                            <ToggleBarIcon icon={scheduledTransferBucketIcons.orphaned}/>
                            <Box sx={toggleIconCounterSx}>{numOprhaned}</Box>
                        </span>
                            </Button>
                            : <Button value="orphaned" disabled={true} sx={toggleSwitchButtonSx}>
                                <ToggleBarIcon icon={faPersonCircleQuestion}/>
                            </Button>}
                    </ToggleButtonGroup>
                </ToggleBar>
            </Stack>
        </PageHeader>
        <PageBody sx={{px: 1.6}}>
            {content}
        </PageBody>
    </PageShell>;
}

export const Route = createFileRoute("/_auth/driver/schedule")({
    component: DriverSchedule,
});
